/**
 * 기능별 사용법 FAQ 데이터
 *
 * 채팅 AI의 기능 안내(lookup_product_help)도 이 내용을 그대로 쓴다.
 * 고친 뒤에는 frontend에서 `npm run export:product-help`로 backend/data/product_help/product_help.json을 다시 만든다.
 */

const IMG = '/images/help';

const figure = (src, alt) =>
  `<figure class="sg__shot"><img src="${src}" alt="${alt}" loading="lazy" decoding="async" width="1400" height="941" /><figcaption>${alt}</figcaption></figure>`;

export const featuresFaq = [
  {
    id: 'features-001',
    category: 'features',
    question: '왓퀴즈로 무엇을 할 수 있나요?',
    answer: `
      <p>왓퀴즈는 선생님 업무용 AI예요. 생기부 작성·검사, 서술형 채점, 평가계획서, 학습지·PPT·이미지, 한글(HWP) 문서, 학부모 문자까지 한곳에서 만들어요.</p>
      <h4>전용 메뉴</h4>
      <ul>
        <li><a href="/create-saenggibu">AI 생기부 생성</a>: 한 명 또는 여러 명(엑셀) 생기부 문장</li>
        <li><a href="/saenggibu-inspector">AI 생기부 검사기</a>: 기재요령 위반·맞춤법 검사와 수정안</li>
        <li><a href="/scoring">AI 서술형 채점</a>: 채점 기준표로 학생 답안 채점</li>
        <li><a href="/assessment-plan">AI 평가계획서</a>: 평가계획서·수업 지도안을 한글 파일로</li>
        <li><a href="/library">라이브러리</a>: 만든 자료 보관, 다른 선생님 자료 나눔</li>
      </ul>
      <h4>AI 채팅에서 고르는 일</h4>
      <ul>
        <li>수업 자료: 학습지, PPT, 이미지</li>
        <li>기록·문서: 생기부, 한글 문서, 행정 문서(기안문·회의록 등)</li>
        <li>소통·상담: 학부모 소통, 상담·마음, 자유 대화</li>
      </ul>
      ${figure(`${IMG}/support-features/chat-menu.jpg`, 'AI 채팅 첫 화면에서 할 일을 고르면 그 일에 맞게 답해요')}
    `,
    tags: ['왓퀴즈 기능', '기능 소개', '할 수 있는 것', 'AI 채팅', '전체 기능'],
    relatedFaqs: ['features-002', 'features-005', 'getting-started-003'],
    lastUpdated: '2026-10-15',
    seoTitle: '왓퀴즈 기능 한눈에 보기 - 생기부·채점·학습지·PPT·한글 문서',
    seoDescription: '왓퀴즈로 할 수 있는 일: AI 생기부 생성·검사, 서술형 채점, 평가계획서, 학습지·PPT·이미지, 한글(HWP) 문서, 학부모 문자.'
  },
  {
    id: 'features-002',
    category: 'features',
    question: 'AI로 생기부는 어떻게 쓰나요? 여러 명도 한 번에 되나요?',
    answer: `
      <p><a href="/create-saenggibu">AI 생기부 생성</a>에서 영역을 고르고 학생 활동을 적으면 2026 기재요령에 맞춘 문장이 나와요. 나이스 바이트도 같이 보여 드려요.</p>
      <h4>한 명씩 쓰기 (단일 생성)</h4>
      <ol>
        <li>영역을 골라요: 행동발달, 교과세특(초·중/고등), 창체 자율, 동아리, 진로</li>
        <li>학생 활동이나 관찰 메모를 적어요</li>
        <li>결과를 복사하거나 "더 구체적으로"처럼 고쳐 달라고 해요</li>
      </ol>
      <h4>여러 명 한 번에 (다중 생성)</h4>
      <ol>
        <li>오른쪽 위 <strong>다중 생성</strong>을 눌러요</li>
        <li>학생 명단과 메모가 든 엑셀을 올리거나 표에 붙여 넣어요</li>
        <li>다 되면 학생별 결과를 확인하고 엑셀로 받아요</li>
      </ol>
      ${figure(`${IMG}/update-2026-09-saenggibu/batch-results.jpg`, '여러 명 생기부가 끝나면 학생별 바이트를 보고 엑셀로 받아요')}
      <h4>다중 생성을 더 편하게</h4>
      <ul>
        <li>결과 아래 <strong>모아두기</strong>를 누르면 그 반 명단이 저장돼요. 다음부터 학생 이름을 자동으로 맞추고, 맞추지 못한 학생만 확인하면 돼요</li>
        <li>결과 위 <strong>반 점검표</strong>가 첫머리가 겹치거나 서로 비슷한 글, 상투어가 많은 학생만 골라 보여 줘요. <strong>이 학생만 다르게</strong>를 누르면 그 학생 글만 고친 안을 만들어요</li>
        <li>교과세특은 <strong>채점 결과 불러오기</strong>로 서술형 채점 기록을 학생별로 가져와 쓸 수 있어요</li>
      </ul>
      <h4>무료와 PRO</h4>
      <ul>
        <li>무료: 한 번에 최대 3명(초등 교과는 4명)</li>
        <li>PRO: 한 번에 30명, 엑셀 다중 생성은 한 번에 150명까지, 자료 파일 첨부</li>
      </ul>
      <p>만든 문장은 <a href="/saenggibu-history">내 생기부 기록</a>에 저장돼요. 기록에서 비슷한 문장을 더 만들 수도 있어요.</p>
    `,
    tags: ['생기부', '생기부 작성', '세특', '행발', '창체', '동아리', '진로', '일괄 생성', '엑셀', '바이트'],
    relatedFaqs: ['features-003', 'features-004', 'getting-started-006'],
    lastUpdated: '2026-10-17',
    seoTitle: 'AI 생기부 작성 방법 - 세특·행발·창체 한 명부터 학급 전체까지',
    seoDescription: '왓퀴즈 AI 생기부 생성 사용법. 2026 기재요령 기준 문장과 나이스 바이트, 엑셀로 여러 학생 생기부를 한 번에 만드는 방법.'
  },
  {
    id: 'features-003',
    category: 'features',
    question: '생기부 검사기는 무엇을 검사하나요?',
    answer: `
      <p><a href="/saenggibu-inspector">AI 생기부 검사기</a>는 쓴 문장을 그해 학교급 기재요령 원문과 대조해 기재하면 안 되는 표현과 맞춤법을 찾아요. 어디가 왜 안 되는지 원문과 쪽수를 같이 보여 주고, 고친 문장도 만들어 드려요.</p>
      ${figure(`${IMG}/support-features/inspector-result.jpg`, '공인어학시험 점수처럼 기재할 수 없는 표현을 기재요령 원문과 함께 짚어 줘요')}
      <h4>쓰는 순서</h4>
      <ol>
        <li>학교급과 작성 당시 학년도·학년을 골라요</li>
        <li>생기부 문장을 붙여 넣고 <strong>검사하기</strong>를 눌러요</li>
        <li>색칠된 부분을 눌러 이유를 보고, AI 수정안을 복사하거나 직접 고쳐요</li>
      </ol>
      <h4>무료와 PRO</h4>
      <ul>
        <li>무료: 한 명씩 붙여 넣어 검사(빠른 모드)</li>
        <li>PRO: 고급 모드, 엑셀·나이스·한글·PDF 파일로 학급 전체를 한 번에 검사</li>
      </ul>
      <p>검사 결과는 <a href="/inspector-history">내 생기부 검사 기록</a>에 남아요.</p>
    `,
    tags: ['생기부 검사기', '기재요령', '기재 금지', '맞춤법', '생기부 점검', '수정안'],
    relatedFaqs: ['features-002', 'getting-started-006', 'features-004'],
    lastUpdated: '2026-10-15',
    seoTitle: 'AI 생기부 검사기 - 기재요령 위반·맞춤법 자동 점검',
    seoDescription: '생기부 문장을 기재요령 원문과 대조해 기재 금지 표현과 맞춤법을 찾고 수정안을 만들어 주는 왓퀴즈 생기부 검사기 사용법.'
  },
  {
    id: 'features-004',
    category: 'features',
    question: '우리 학교 생기부 문체로 쓰게 할 수 있나요?',
    answer: `
      <p>네. <a href="/saenggibu-my-style">내 스타일</a>에 우리 학교 우수 예시나 작성 지침을 넣으면 AI가 문체를 익혀 생기부를 쓸 때 자동으로 적용해요.</p>
      <ol>
        <li>내 스타일에서 영역(행동발달·교과·창체·동아리·진로)을 골라요</li>
        <li>예시 문장을 붙여 넣거나 파일을 올려 분석해요</li>
        <li>저장하면 AI 생기부 생성에서 <strong>내 스타일 적용 중</strong>으로 켜져요</li>
      </ol>
      ${figure(`${IMG}/update-2026-09-saenggibu/style-saved.jpg`, '저장한 스타일은 생기부를 만들 때 자동으로 적용돼요')}
      <p>예전에 쓴 생기부를 붙여 넣고 <strong>내 스타일로 바로 적용</strong>을 누르면 분석과 저장이 한 번에 끝나요.</p>
      <p>무료도 모든 생기부에 쓰는 <strong>공통 스타일 1개</strong>를 저장해 쓸 수 있어요(분석 3번). 영역마다 다른 스타일은 PRO에서 저장할 수 있어요.</p>
    `,
    tags: ['내 스타일', '학교 스타일', '생기부 문체', '우수 예시', '작성 지침'],
    relatedFaqs: ['features-002', 'features-003', 'features-012'],
    lastUpdated: '2026-10-17',
    seoTitle: '생기부 내 스타일 - 우리 학교 문체를 AI가 익히게 하기',
    seoDescription: '우리 학교 생기부 우수 예시와 지침을 넣으면 AI가 문체를 익혀 자동 적용하는 왓퀴즈 내 스타일 기능 사용법.'
  },
  {
    id: 'features-005',
    category: 'features',
    question: '서술형 답안을 AI로 채점할 수 있나요?',
    answer: `
      <p>네. <a href="/scoring">AI 서술형 채점</a>에 채점 기준표와 학생 답안을 넣으면 항목별 점수와 근거가 나와요. 근거는 답안의 어느 문장을 보고 줬는지 색으로 짚어 줘요.</p>
      ${figure(`${IMG}/update-2026-09-scoring/result.jpg`, '항목별 점수와 평가 근거를 한 화면에서 봐요')}
      <h4>채점 순서</h4>
      <ol>
        <li>채점 기준(루브릭)을 적거나 파일로 올려요</li>
        <li>학생 답안을 넣어요: 글, 사진, PDF, 스캔 파일</li>
        <li>점수를 확인하고 필요하면 직접 고치거나 AI에게 다시 요청해요</li>
      </ol>
      <h4>여러 명 채점</h4>
      <ul>
        <li>한 파일에 여러 학생 답안이 있어도 학생별로 나눠 채점해요(한 번에 50명)</li>
        <li><strong>학생이 직접 올리기</strong>를 고르면 제출 링크를 만들어 학생이 답안을 올려요</li>
        <li><a href="/scoring-history">채점 기록</a>에서 여러 평가를 골라 <strong>교과세특 쓰기</strong>를 누르면 학생별로 모아 교과세특을 만들어요. 점수·등급은 넘기지 않아요</li>
      </ul>
      <p>파일 답안과 여러 명 채점은 PRO 기능이에요. 결과는 <a href="/scoring-history">채점 기록</a>에 저장돼요.</p>
    `,
    tags: ['서술형 채점', 'AI 채점', '루브릭', '채점 기준', '채점 기준표', '수행평가 채점', '답안 스캔', '제출방'],
    relatedFaqs: ['features-006', 'features-002', 'getting-started-003'],
    lastUpdated: '2026-10-17',
    seoTitle: 'AI 서술형 채점 - 루브릭으로 학생 답안 채점과 근거 확인',
    seoDescription: '채점 기준표와 학생 답안(사진·PDF·스캔)을 넣으면 항목별 점수와 근거를 보여 주는 왓퀴즈 AI 서술형 채점 사용법.'
  },
  {
    id: 'features-006',
    category: 'features',
    question: '평가계획서나 수업 지도안도 만들 수 있나요?',
    answer: `
      <p>네. <a href="/assessment-plan">AI 평가계획서</a>에서 과목과 성취기준을 고르면 평가계획서 초안을 만들고 한글(HWP) 파일로 받을 수 있어요.</p>
      <h4>만들 수 있는 것</h4>
      <ul>
        <li>평가계획서, 서·논술형 평가 계획서</li>
        <li>수업 지도안, 프로젝트 수업 계획서</li>
        <li>탐구 질문, 학교자율시간 계획서</li>
      </ul>
      ${figure(`${IMG}/update-2026-09-curriculum/assessment-draft.jpg`, '성취기준을 고르면 평가계획서 초안이 나와요')}
      <p>성취기준은 2015·2022 개정 교육과정 원문에서 찾아 넣어요. 작년 계획서 파일을 올려 고치게 하려면 PRO가 필요해요.</p>
    `,
    tags: ['평가계획서', '수업 지도안', '성취기준', '수행평가 계획', '한글 파일', '프로젝트 수업'],
    relatedFaqs: ['features-005', 'features-009', 'features-002'],
    lastUpdated: '2026-10-15',
    seoTitle: 'AI 평가계획서·수업 지도안 만들기 - 성취기준으로 한글 파일까지',
    seoDescription: '성취기준을 고르면 평가계획서·수업 지도안 초안을 만들고 한글(HWP) 파일로 받는 왓퀴즈 AI 평가계획서 사용법.'
  },
  {
    id: 'features-007',
    category: 'features',
    question: '수업 PPT는 어떻게 만들고 고치나요?',
    answer: `
      <p><a href="/chat?category=ppt_maker">AI 채팅의 PPT</a>에서 주제와 학년을 적으면 슬라이드 구성을 먼저 보여 드리고, 확인하면 PPT를 만들어요.</p>
      <ol>
        <li>예: "중2 과학 광합성 수업 PPT 10장"</li>
        <li>슬라이드 구성을 보고 빼거나 더할 내용을 말해요</li>
        <li>만들어진 PPT에서 글자를 바로 고치거나 "3번 장 그림 바꿔줘"처럼 요청해요</li>
      </ol>
      ${figure(`${IMG}/update-2026-09-ppt-edit/editing.jpg`, '만든 PPT의 글자를 화면에서 바로 고쳐요')}
      <h4>받기</h4>
      <ul>
        <li><strong>텍스트형 PPT</strong>는 PPTX로 받아 파워포인트에서 글자를 고칠 수 있어요</li>
        <li><strong>이미지형 PPT</strong>는 슬라이드가 그림이라 파워포인트에서 글자를 고칠 수 없어요</li>
      </ul>
      <p>무료는 텍스트형 PPT를 8장까지 만들어 PPTX로 받을 수 있고, 이미지형 PPT와 40장까지 만들기는 PRO예요. 만든 PPT는 <a href="/library?tab=my">라이브러리</a>에 자동 저장돼요.</p>
    `,
    tags: ['PPT 만들기', 'AI PPT', '수업 PPT', 'PPTX', '슬라이드 수정', '이미지형 PPT'],
    relatedFaqs: ['features-008', 'features-011', 'worksheet-001'],
    lastUpdated: '2026-10-15',
    seoTitle: 'AI 수업 PPT 만들기와 수정 방법 - PPTX로 받기',
    seoDescription: '주제만 적으면 수업 PPT 구성부터 슬라이드까지 만들고, 글자를 바로 고쳐 PPTX로 받는 왓퀴즈 AI PPT 사용법.'
  },
  {
    id: 'features-008',
    category: 'features',
    question: '시험지나 학습지를 한글(HWP) 파일로 받을 수 있나요?',
    answer: `
      <p>네. <a href="/chat?category=worksheet_gen">AI 채팅의 학습지</a>에서 학년·과목·단원·문항 수를 적으면 문제와 정답지를 한글 파일로 만들어 드려요.</p>
      ${figure(`${IMG}/update-2026-09-exam/hwpx-panel.jpg`, '만든 시험지를 한글 파일로 미리 보고 받아요')}
      <ul>
        <li>객관식·서술형·빈칸·OX, 형성평가·단원평가, 수행평가와 루브릭</li>
        <li>기본은 <strong>글자 편집형</strong>이라 받은 한글 파일에서 글자를 직접 고칠 수 있어요</li>
        <li>채팅으로 "3번 문제 더 쉽게"처럼 말하면 파일도 같이 바뀌어요</li>
      </ul>
      <p>무료는 텍스트 학습지를 한 번에 10문항까지, PRO는 40문항과 이미지형 학습지까지 만들어요.</p>
    `,
    tags: ['시험지', '학습지', '한글 파일', 'HWP', 'HWPX', '다운로드', '정답지', '단원평가'],
    relatedFaqs: ['worksheet-001', 'worksheet-003', 'features-009'],
    lastUpdated: '2026-10-15',
    seoTitle: 'AI 시험지·학습지를 한글(HWP) 파일로 받는 방법',
    seoDescription: '학년·과목·단원만 적으면 문제와 정답지를 한글 파일로 만들고 채팅으로 고치는 왓퀴즈 AI 학습지 사용법.'
  },
  {
    id: 'features-009',
    category: 'features',
    question: '한글(HWP) 양식을 올리면 AI가 채워 주나요?',
    answer: `
      <p>네. PRO에서는 한글(HWP·HWPX) 양식을 올리고 채울 내용을 말하면 원본 서식 그대로 칸을 채우거나 글자를 고쳐 드려요. 가정통신문·동의서·안내문처럼 처음부터 새로 만들 수도 있어요.</p>
      ${figure(`${IMG}/update-2026-09-hwp-doc/editor.jpg`, '채운 한글 문서를 화면에서 확인하고 바로 받아요')}
      <ol>
        <li><a href="/chat?category=document">AI 채팅의 한글 문서</a>를 골라요</li>
        <li>양식 파일을 올리고 "학생 명단으로 채워줘"처럼 요청해요</li>
        <li>화면에서 확인하고 한글 파일로 받아요</li>
      </ol>
      <ul>
        <li>자유·행정·학부모·상담 대화에서도 첨부한 한글 파일을 고칠 수 있어요</li>
        <li>PDF는 원본 글자를 고칠 수 없어요. 고친 내용으로 한글·워드 파일을 새로 만들어 드려요</li>
      </ul>
    `,
    tags: ['한글 문서', 'HWP', 'HWPX', '양식 채우기', '가정통신문', '공문', '행정 문서'],
    relatedFaqs: ['features-008', 'features-012', 'worksheet-003'],
    lastUpdated: '2026-10-15',
    seoTitle: 'AI 한글(HWP) 양식 채우기 - 서식 그대로 문서 작성',
    seoDescription: '한글 양식을 올리면 원본 서식 그대로 칸을 채우고 고쳐 주는 왓퀴즈 AI 한글 문서 사용법. 가정통신문·동의서·안내문도 새로 만들어요.'
  },
  {
    id: 'features-010',
    category: 'features',
    question: '수업용 이미지도 만들 수 있나요?',
    answer: `
      <p>네. <a href="/chat?category=image_generation">AI 채팅의 이미지</a>에서 원하는 그림을 말로 설명하면 바로 그려 드려요. 자유·생기부·행정·학부모·상담 대화에서도 답변 아래에 그림을 그려요.</p>
      <ul>
        <li>예: "초등 3학년 물의 순환 그림, 단계별 화살표와 이름표"</li>
        <li>한 번에 4장까지 그리고, "배경을 하얗게"처럼 말해 고쳐요</li>
        <li>수학 그래프·도형은 정확한 그림으로 그려요</li>
      </ul>
      ${figure(`${IMG}/update-2026-10-mathfig/graph-function.jpg`, '함수 그래프처럼 정확해야 하는 그림도 그려요')}
      <p>무료는 기본 품질, PRO는 고품질 생성과 내 사진을 참고한 그림이 돼요. 만든 이미지는 <a href="/library?tab=my">라이브러리</a>에 저장돼요.</p>
    `,
    tags: ['이미지 생성', 'AI 그림', '수업 이미지', '수학 그래프', '도형', '삽화'],
    relatedFaqs: ['features-007', 'features-013', 'features-001'],
    lastUpdated: '2026-10-15',
    seoTitle: 'AI 수업 이미지 만들기 - 삽화·그래프·도형',
    seoDescription: '말로 설명하면 수업용 삽화, 수학 그래프와 도형을 그려 주는 왓퀴즈 AI 이미지 생성 사용법과 무료·PRO 차이.'
  },
  {
    id: 'features-011',
    category: 'features',
    question: '어떤 AI 모델을 쓸 수 있나요?',
    answer: `
      <p>PRO는 채팅 화면 위쪽 모델 선택에서 GPT·Claude·Gemini 최신 모델을 골라 써요. 대화 중에 바꿔도 앞의 대화가 이어져요. 무료는 빠른 기본 모델로 답해요.</p>
      ${figure(`${IMG}/update-2026-10-gpt61/picker.jpg`, '채팅 위쪽에서 모델을 골라요')}
      <ul>
        <li>최신 정보가 필요한 질문은 웹에서 찾아 출처와 함께 답해요(무료도 돼요)</li>
      </ul>
    `,
    tags: ['AI 모델', 'GPT', 'Claude', 'Gemini', '모델 선택', '웹 검색'],
    relatedFaqs: ['payment-001', 'features-001', 'features-012'],
    lastUpdated: '2026-10-15',
    seoTitle: '왓퀴즈에서 쓸 수 있는 AI 모델 - GPT·Claude·Gemini',
    seoDescription: '왓퀴즈 PRO는 GPT·Claude·Gemini 최신 모델을 채팅에서 골라 쓸 수 있어요. 모델 고르는 위치와 웹 검색 안내.'
  },
  {
    id: 'features-012',
    category: 'features',
    question: '파일은 몇 개, 얼마나 큰 것까지 올릴 수 있나요?',
    answer: `
      <p>채팅에는 한 번에 파일 10개까지 올릴 수 있어요. PDF는 200MB, 한글·워드·엑셀·PPT 같은 문서는 100MB, 사진은 한 장 20MB까지예요. 문서·이미지 파일 첨부는 PRO 기능이에요.</p>
      <ul>
        <li>문서: PDF, 한글(HWP·HWPX), 워드, 엑셀, PPT, CSV, 텍스트</li>
        <li>이미지: JPG, PNG, HEIC 등</li>
        <li>교과서처럼 수백 쪽인 파일도 원본을 다 보관해 두고, 질문에 맞는 쪽을 찾아 읽어요. 쪽이나 단원을 말해 주시면 그 부분을 정확히 읽어요</li>
        <li>한 번에 AI가 함께 읽는 분량은 약 20만 자예요. 파일이 많아 다 못 읽으면 AI가 필요한 파일을 직접 더 읽고 답해요</li>
        <li>엑셀·CSV의 평균·인원·비율·t검정은 원본 표 전체로 계산해요</li>
        <li>여러 파일을 같이 올리면 비교하거나 합쳐서 답해요</li>
      </ul>
      ${figure(`${IMG}/update-2026-10-bigfile/chat-page-answer.jpg`, '긴 교재 PDF를 올리고 그 내용으로 문제를 만들어요')}
      <p>녹음·영상 파일 받아쓰기와 유튜브·웹 링크 요약은 로그인만 하면 무료로도 돼요.</p>
    `,
    tags: ['파일 첨부', '파일 크기', '업로드 한도', 'PDF', '한글 파일', '엑셀', '이미지 첨부'],
    relatedFaqs: ['features-013', 'features-009', 'technical-001'],
    lastUpdated: '2026-10-19',
    seoTitle: '왓퀴즈 파일 첨부 안내 - 개수·크기·지원 형식',
    seoDescription: '왓퀴즈 채팅 파일 첨부: 한 번에 10개, PDF 200MB·문서 100MB·사진 20MB, PDF·한글·워드·엑셀·PPT·이미지 지원. 녹음·링크는 무료로도 가능.'
  },
  {
    id: 'features-013',
    category: 'features',
    question: '녹음·영상·유튜브 링크로 자료를 만들 수 있나요?',
    answer: `
      <p>네. 회의·수업 녹음이나 영상 파일을 올리면 받아쓴 뒤 요약·회의록·학습지로 정리해 드려요. 유튜브나 웹 주소를 붙여 넣어도 내용을 읽고 정리해요. 로그인만 하면 무료로도 돼요.</p>
      ${figure(`${IMG}/update-2026-09-ingest/audio-result.jpg`, '녹음 파일을 받아써서 회의록으로 정리해요')}
      <ul>
        <li>녹음: MP3, M4A, WAV 등 / 영상: MP4, MOV, WEBM 등</li>
        <li>입력창의 마이크를 누르면 말로 질문할 수도 있어요</li>
      </ul>
    `,
    tags: ['녹음', '받아쓰기', '회의록', '유튜브', '링크 요약', '음성 입력'],
    relatedFaqs: ['features-012', 'features-008', 'features-001'],
    lastUpdated: '2026-10-15',
    seoTitle: '녹음·영상·유튜브로 회의록과 수업 자료 만들기',
    seoDescription: '녹음·영상 파일과 유튜브·웹 링크를 받아써 요약·회의록·학습지로 정리하는 왓퀴즈 기능. 로그인하면 무료.'
  },
  {
    id: 'features-014',
    category: 'features',
    question: '만든 자료는 어디에 저장되나요?',
    answer: `
      <p>로그인하고 만든 PPT·학습지·이미지는 <a href="/library?tab=my">라이브러리 &gt; 내 자료</a>에 자동으로 저장돼요. 채팅에서 만든 한글·엑셀·워드·PDF 파일은 답변 아래 파일 카드에서 받고, 받은 파일은 라이브러리 기타에 남아요.</p>
      ${figure(`${IMG}/support-features/library.jpg`, '라이브러리에서 내 자료를 종류별로 찾아요')}
      <ul>
        <li>생기부: <a href="/saenggibu-history">내 생기부 기록</a></li>
        <li>검사: <a href="/inspector-history">내 생기부 검사 기록</a></li>
        <li>채점: <a href="/scoring-history">채점 기록</a></li>
        <li>평가계획서: <a href="/assessment-plan-history">계획서 기록</a></li>
        <li>채팅: 왼쪽 <strong>내 대화 목록</strong></li>
      </ul>
      <p>라이브러리 <strong>나눔터</strong>에서는 다른 선생님이 나눈 자료를 보고 받을 수 있어요. 로그인하지 않고 만든 자료는 저장되지 않아요.</p>
    `,
    tags: ['라이브러리', '저장 위치', '내 자료', '나눔터', '공유 삭제', '기록', '어디 저장', '다시 받기'],
    relatedFaqs: ['features-007', 'features-008', 'features-001'],
    lastUpdated: '2026-10-15',
    seoTitle: '왓퀴즈에서 만든 자료 저장 위치 - 라이브러리와 기록',
    seoDescription: 'PPT·학습지·이미지는 라이브러리 내 자료에, 생기부·채점·검사·계획서는 각 기록 메뉴에 저장돼요. 나눔터 자료 공유 안내.'
  },
  {
    id: 'features-015',
    category: 'features',
    question: 'AI가 제 정보를 기억하게 할 수 있나요?',
    answer: `
      <p>네. <a href="/chat/settings">AI 맞춤 설정</a>에 학교급·과목·담당 학년과 원하는 답변 방식을 적어 두면 모든 대화에 반영돼요.</p>
      ${figure(`${IMG}/support-features/chat-settings.jpg`, 'AI 맞춤 설정에서 선생님 정보와 답변 방식을 정해요')}
      <ul>
        <li><strong>AI가 기억하는 내용</strong>: 대화에서 알게 된 것을 기억해요(무료 30개, PRO 100개). 보고 지울 수 있어요</li>
        <li><strong>참고 자료</strong>: 교과서 내용이나 학교 지침을 넣어 두면 답할 때 참고해요(무료 글 3개, PRO 파일 포함 500개)</li>
        <li><strong>내 프롬프트</strong>: 자주 쓰는 요청을 저장해 두고 바로 꺼내 써요</li>
      </ul>
      <p>기억이 필요 없으면 맨 위 <strong>AI 맞춤 답변</strong>을 꺼요.</p>
    `,
    tags: ['AI 맞춤 설정', '기억', '메모리', '참고 자료', '내 프롬프트', '개인화'],
    relatedFaqs: ['features-004', 'features-011', 'features-001'],
    lastUpdated: '2026-10-15',
    seoTitle: 'AI 맞춤 설정 - 선생님 정보·기억·참고 자료·내 프롬프트',
    seoDescription: '학교급·과목과 답변 방식을 저장하고, AI 기억·참고 자료·내 프롬프트를 관리하는 왓퀴즈 AI 맞춤 설정 사용법.'
  },
  {
    id: 'features-016',
    category: 'features',
    question: '학부모 문자나 민원 답변도 도와주나요?',
    answer: `
      <p>네. <a href="/chat?category=parent_communication">AI 채팅의 학부모 소통</a>에서 상황을 적으면 바로 보낼 수 있는 문자와 통화 준비 내용을 만들어 드려요.</p>
      <ul>
        <li>칭찬·안내 문자, 상담 일정 안내</li>
        <li>민원 답변: 사실 확인, 공감, 학교가 할 수 있는 일과 없는 일을 나눠 정중하게</li>
        <li>상담 준비: 꺼낼 말과 기록할 내용</li>
      </ul>
      <p>학생·학부모 이름 같은 개인정보는 빼고 적어 주세요. 마음이 힘들 때는 <a href="/chat?category=counseling_care">상담·마음</a>에서 이야기해도 돼요.</p>
    `,
    tags: ['학부모 문자', '민원 대응', '학부모 상담', '가정 연락', '상담 준비'],
    relatedFaqs: ['features-009', 'features-001', 'features-015'],
    lastUpdated: '2026-10-15',
    seoTitle: '학부모 문자·민원 답변 AI로 쓰기',
    seoDescription: '상황만 적으면 학부모 칭찬·안내 문자, 민원 답변, 상담 준비 내용을 만들어 주는 왓퀴즈 학부모 소통 사용법.'
  },
  {
    id: 'features-017',
    category: 'features',
    question: '왓퀴즈 연수원은 무엇인가요?',
    answer: `
      <p><a href="/training">왓퀴즈 연수원</a>은 현직 선생님의 교실 실전 노하우를 온라인으로 듣는 자율연수예요. 학점이나 나이스 등재 연수가 아닌 실무 중심 연수예요.</p>
      <ul>
        <li>강의와 함께 바로 쓰는 자료, PRO 혜택을 드려요</li>
        <li>신청한 연수는 <a href="/my/trainings">내 연수</a>에서 이어서 들어요</li>
        <li>학교로 찾아가는 무료 AI 연수는 <a href="/training/visit">연수 신청</a>에서 신청해요</li>
      </ul>
    `,
    tags: ['연수원', '자율연수', 'AI 연수', '교사 연수', '찾아가는 연수'],
    relatedFaqs: ['features-001', 'payment-001', 'getting-started-001'],
    lastUpdated: '2026-10-15',
    seoTitle: '왓퀴즈 연수원 - 교사 AI 활용 온라인 자율연수',
    seoDescription: '현직 교사의 AI 활용 노하우를 듣는 왓퀴즈 온라인 자율연수와 학교로 찾아가는 무료 AI 연수 신청 안내.'
  }
];

export const searchFeaturesFaq = (query) => {
  const searchTerm = query.toLowerCase();
  return featuresFaq.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm) ||
    faq.answer.toLowerCase().includes(searchTerm) ||
    faq.tags.some(tag => tag.toLowerCase().includes(searchTerm))
  );
};
